import { css, styled } from '@mui/material';
import { np, useTranslation } from '@kc-console/core';

import { CheckboxItem, checkboxItemClasses } from '../CheckboxItem';
import { Switch, switchClasses } from '../Switch';
import { Typography } from '../Typography';
import { menuContainerToolbarClasses } from './theme';

import type { MouseEvent } from 'react';
import type { MenuContainerToolbarProps } from './types';

export const MENU_CONTAINER_TOOLBAR_SELECT_ALL_KEY = 'menu-container-toolbar-select-all-checkbox';

export default function MenuContainerToolbar(props: MenuContainerToolbarProps) {
  const {
    isSearched,
    isSelectAllCheckboxFocused,
    onSelectAllChange,
    onSwitchChange,
    selectAllChecked,
    selectAllIndeterminate,
    selectedCount,
    showSelectAllCheckbox,
    showSwitch = false,
    switchChecked,
    switchLabel: switchLabelProp,
    ...others
  } = props;
  const { t } = useTranslation();
  const switchLabel = switchLabelProp ?? t('ui:menu_container_toolbar.selectable_switch_text');
  const handleCheckboxItemClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLElement) {
      const checkbox = e.target.querySelector('input[type="checkbox"]');
      if (checkbox instanceof HTMLInputElement) {
        checkbox.click();
      }
    }
  };

  return (
    <MenuContainerToolbarRoot showSwitch={showSwitch} {...others}>
      {showSelectAllCheckbox ? (
        <MenuContainerToolbarCheckboxItem
          data-menu-item-key={MENU_CONTAINER_TOOLBAR_SELECT_ALL_KEY}
          checked={selectAllChecked}
          indeterminate={selectAllIndeterminate}
          onChange={onSelectAllChange}
          text={
            isSearched
              ? `${t('ui:menu_container_toolbar.select_all_text_searched')}${selectedCount ? ` (${selectedCount})` : ''}`
              : `${t('ui:menu_container_toolbar.select_all_text')}${selectedCount ? ` (${selectedCount})` : ''}`
          }
          showSwitch={showSwitch}
          focused={!!isSelectAllCheckboxFocused}
          onClick={handleCheckboxItemClick}
          slotProps={{ checkbox: { tabIndex: -1 } }}
        />
      ) : (
        <MenuContainerToolbarText variant="body/normal/md">
          {isSearched
            ? `${t('ui:menu_container_toolbar.search_result_text')}${selectedCount ? ` (${selectedCount})` : ''}`
            : `${t('common:common.label.all')} ${selectedCount ? ` (${selectedCount})` : ''}`}
        </MenuContainerToolbarText>
      )}

      {showSwitch && (
        <MenuContainerToolbarSwitch
          checked={switchChecked}
          onChange={onSwitchChange}
          text={switchLabel}
        />
      )}
    </MenuContainerToolbarRoot>
  );
}

const MenuContainerToolbarRoot = styled('div', {
  target: menuContainerToolbarClasses.root,
  shouldForwardProp: np('showSwitch'),
})<{ showSwitch: boolean }>(
  ({ showSwitch }) => css`
    display: flex;
    align-items: center;
    justify-content: ${showSwitch ? 'space-between' : 'flex-start'};
    min-height: 42px;
    gap: 8px;
  `,
);

const MenuContainerToolbarText = styled(Typography, {
  target: menuContainerToolbarClasses.text,
})``;

const MenuContainerToolbarCheckboxItem = styled(CheckboxItem, {
  target: menuContainerToolbarClasses.checkboxItem,
  shouldForwardProp: np('focused', 'showSwitch'),
})<{ focused: boolean; showSwitch: boolean }>(
  ({ theme, focused, showSwitch }) => css`
    .${checkboxItemClasses.label} {
      white-space: nowrap;
    }
    ${!showSwitch &&
    css`
      flex: 1 0 auto;
      .${checkboxItemClasses.label} {
        flex: 1;
      }
    `}
    ${focused &&
    css`
      position: relative;
      ${theme.mixins.focusRing('inside', {
        selectors: {
          focus: '&',
        },
      })}
      &:before {
        margin-left: -16px;
      }
    `}
  `,
);

const MenuContainerToolbarSwitch = styled(Switch, {
  target: menuContainerToolbarClasses.switch,
})`
  min-width: 0px;
  .${switchClasses.textContainer} {
    overflow: hidden;
  }
  .${switchClasses.stateText} {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
